import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Button, toast } from '@utima/ui';
import { Input, type TypedFormState } from '@utima/ui-informed';
import { jwtDecode, type JwtPayload } from 'jwt-decode';

import { PasswordFields } from '@/components/fields/passwordFields/PasswordFields';
import { BasicForm } from '@/components/form/BasicForm';
import { trpc } from '@/services/trpc';

export const Route = createFileRoute('/_authLayout/reset-password/')({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): { token: string } => {
    return {
      token: (search.token as string) || '',
    };
  },
});

export function ResetPasswordPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const changePasswordMutation = trpc.auth.changePassword.useMutation();

  const initialValues = {
    email: '',
    password: '',
    confirmPassword: '',
  };

  let exp: number = 0;

  try {
    const jwtPayload = jwtDecode<JwtPayload & { email: string }>(token);
    initialValues.email = jwtPayload.email;
    exp = jwtPayload.exp!;
  } catch (e) {
    console.error(e);
  }

  const onSubmit = (formState: TypedFormState<typeof initialValues>) => {
    changePasswordMutation.mutate(
      {
        token: token,
        email: formState.values.email,
        password: formState.values.password,
      },
      {
        onError: (error: unknown) => {
          console.error(error);
          toast.error(t`Password Change`, {
            description: t`Failed to change password. Please try again later.`,
          });
        },
        onSuccess: () => {
          toast.success(t`Password Change`, {
            description: t`Password change successful. You can now log in.`,
          });

          navigate({ to: '/login' });
        },
      },
    );
  };

  // Token is either expired or invalid
  if (!token || !initialValues.email || exp < Date.now() / 1000) {
    return (
      <div className='flex flex-col items-center gap-6'>
        <h1 className='text-center text-2xl font-bold'>
          <Trans>Change password</Trans>
        </h1>
        <div className='text-center text-sm'>
          <Trans>
            Token is invalid or has expired. Try logging in again or request a
            password reset.
          </Trans>
        </div>
        <div className='flex justify-center'>
          <Button
            data-cta='admin-reset-password-back-to-login-button'
            variant='ghost'
            outline
            type='submit'
            asChild
          >
            <Link to='/login' className='text-sm'>
              <Trans>Back to login</Trans>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <BasicForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      className='flex w-full flex-col gap-6'
    >
      <h1 className='text-center text-2xl font-bold'>
        <Trans>Change password</Trans>
      </h1>

      <Input
        name='email'
        type='email'
        label={t`Email`}
        data-cta='admin-reset-password-email-input'
        disabled
        required
      />

      <PasswordFields title={t`Enter password`} hasOldPassword={false} />

      <div className='flex justify-between gap-4'>
        <Button
          data-cta='admin-reset-password-back-to-login-button'
          variant='muted'
          outline
          asChild
        >
          <Link to='/login'>
            <Trans>Back to login</Trans>
          </Link>
        </Button>
        <Button
          data-cta='admin-reset-password-submit-button'
          variant='primary'
          type='submit'
        >
          <Trans>Change password</Trans>
        </Button>
      </div>
    </BasicForm>
  );
}
